import { useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type {
  LandingRouletteHistoryItem,
  LandingSpinResult,
} from '@/lib/landing-roulette'
import { formatDateTime, formatRouletteHistoryPrize } from './landing-profile-format'
import styles from './LandingNumbersPage.module.css'
import couponStyles from './LandingScratchCardModal.module.css'
import prizeStyles from './LandingScratchPrizes.module.css'

export function LandingScratchPrizes({
  prizes,
  onUseCoupon,
  salesOpen,
  isLoading,
}: {
  prizes: LandingRouletteHistoryItem[]
  onUseCoupon: (result: LandingSpinResult) => Promise<void>
  salesOpen: boolean
  isLoading: boolean
}) {
  const [using, setUsing] = useState<number | null>(null)
  const busy = useRef(false)
  const [error, setError] = useState('')
  const scratchPrizes = prizes.filter(
    (prize) =>
      ['scratch_card', 'achievement', 'purchase_scratch_card'].includes(
        prize.prize_payload?.source ?? ''
      ) && prize.status === 'pending'
  )
  if (!scratchPrizes.length) return null
  async function applyCoupon(prize: LandingRouletteHistoryItem) {
    if (busy.current) return
    busy.current = true
    setUsing(prize.id)
    setError('')
    try {
      await onUseCoupon({
        position: 0,
        prize_type: 'coupon',
        prize_payload: prize.prize_payload,
      })
    } catch {
      setError('Não foi possível abrir a compra com seu cupom. Tente novamente.')
    } finally {
      busy.current = false
      setUsing(null)
    }
  }
  return (
    <section className={prizeStyles.section} aria-labelledby="scratch-prizes-title">
      <h2 id="scratch-prizes-title" className={styles.subheading}>
        Meus prêmios extras
      </h2>
      <div className={prizeStyles.list}>
        {scratchPrizes.map((prize) => (
          <article className={prizeStyles.card} key={prize.id}>
            <div className={prizeStyles.header}>
              <Icon
                icon={
                  prize.prize_type === 'coupon'
                    ? 'mdi:ticket-percent-outline'
                    : 'mdi:gift-outline'
                }
                className={prizeStyles.icon}
                aria-hidden
              />
              <div className={prizeStyles.details}>
                {prize.prize_payload?.source === 'achievement' && (
                  <p className={prizeStyles.achievement}>
                    Conquista: {prize.prize_payload.label}
                  </p>
                )}
                <strong className={styles.rouletteHistoryPrize}>
                  {formatRouletteHistoryPrize(prize)}
                </strong>
                <span className={styles.rouletteHistoryDate}>
                  {formatDateTime(prize.created_at)}
                </span>
              </div>
            </div>
            {prize.prize_type === 'coupon' && (
              <>
                <code className={prizeStyles.code}>{prize.prize_payload?.code}</code>
                {prize.status === 'pending' && salesOpen && (
                  <button
                    type="button"
                    className={`${couponStyles.action} ${prizeStyles.action}`}
                    disabled={using !== null || isLoading}
                    onClick={() => void applyCoupon(prize)}
                  >
                    {using === prize.id
                      ? 'Aplicando cupom…'
                      : 'Usar cupom na próxima compra'}
                  </button>
                )}
              </>
            )}
          </article>
        ))}
      </div>
      {error && (
        <p className={couponStyles.error} role="alert">
          {error}
        </p>
      )}
    </section>
  )
}
